import { useMemo } from "react"

import { useCurrentOverlay } from "shared/context/overlay/OverlayIDContext"
import { useEvent } from "shared/lib/useEvent"
import { ProjectChild } from "shared/types/Project"

import {
  MusterheftExportSelection,
  useMusterheftExport
} from "entities/projects/model/useMusterheftExport"
import { useAggregatedSharedProjectData } from "features/project-sharing/api/useAggregatedSharedProjectData"

import { MusterheftExportOverlayModel } from "./useMusterheftExportOverlayModel"

interface UseSharedMusterheftExportOverlayModelArgs {
  projectShareID: string
}

/**
 * Model for the Musterheft export overlay opened on a shared project link.
 * Loads the project's children via the share-link endpoint and exports through
 * the share export URL. Returns the same MusterheftExportOverlayModel shape as
 * the owned variant so both feed the same overlay component.
 */
export function useSharedMusterheftExportOverlayModel({
  projectShareID
}: UseSharedMusterheftExportOverlayModelArgs): MusterheftExportOverlayModel {
  const { closeOverlay } = useCurrentOverlay()
  const { exportMusterheft } = useMusterheftExport(projectShareID, true)

  const { projectData } = useAggregatedSharedProjectData({
    projectShareID,
    options: { includePrice: false }
  })

  const projectChildren: ProjectChild[] = useMemo(
    () => projectData.project?.data?.children ?? [],
    [projectData.project?.data?.children]
  )

  const isLoading = !!projectData.project?.isLoading

  const handleExport = useEvent((selection: MusterheftExportSelection) => {
    exportMusterheft(selection)
    closeOverlay()
  })

  return {
    projectID: projectShareID,
    projectChildren,
    isLoading,
    onCancel: closeOverlay,
    onExport: handleExport
  }
}
